<template>
    <Transition name="modal-fade">
        <div v-if="isVisible" class="cf-overlay" @click.self="close">
            <Transition name="modal-slide">
                <div v-if="isVisible" class="cf-dialog cf-dialog--lg" @click.stop>

                    <!-- ========== HEADER ========== -->
                    <div class="cf-modal-head cf-dialog-head">
                        <div class="cf-modal-head__main">
                            <span class="cf-modal-head__icon"><i class="ri-archive-2-line"></i></span>
                            <div>
                                <h4 class="cf-dialog-title">{{ titleModal }}</h4>
                                <p v-if="filterText" class="cf-modal-head__sub">{{ filterText }}</p>
                            </div>
                        </div>
                        <button @click="close" type="button" class="cf-dialog-close" aria-label="Fermer"><i class="ri-close-line"></i></button>
                    </div>

                    <!-- ========== TOOLBAR ========== -->
                    <div class="cf-dialog-toolbar">
                        <div class="cf-toolbar-row">
                            <div class="search-wrap">
                                <i class="ri-search-line search-icon"></i>
                                <input v-model="searchQuery" ref="searchInput" type="search" class="search-input"
                                       placeholder="Rechercher un article/service..." @input="handleSearchDebounced">
                                <button v-if="searchQuery" @click="clearSearch" class="search-clear" aria-label="Effacer"><i class="ri-close-line"></i></button>
                            </div>
                            <button v-if="refreshable" @click="handleRefresh" :disabled="isRefreshing" class="ti-btn ti-btn-light" title="Recharger la liste des articles depuis le serveur">
                                <i class="ri-refresh-line align-middle"></i> {{ isRefreshing ? 'Actualisation...' : 'Actualiser' }}
                            </button>
                            <button v-if="showAddButton" @click="handleAdd" class="ti-btn ti-btn-primary-full">
                                <i class="ri-add-line align-middle"></i> Ajouter
                            </button>
                        </div>
                        <div class="cf-toolbar-meta">
                            <p class="cf-count"><span>{{ filteredData.length }}</span> article{{ filteredData.length > 1 ? 's' : '' }} trouvé{{ filteredData.length > 1 ? 's' : '' }}</p>
                            <div class="cf-perpage">
                                <label>Afficher</label>
                                <div class="cf-select-wrap">
                                    <select v-model="perPage" class="cf-inp cf-select cf-select--sm">
                                        <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                                    </select>
                                    <i class="ri-arrow-down-s-line cf-select-arrow"></i>
                                </div>
                                <span>par page</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="stockError" class="cf-stock-error">
                        <i class="ri-error-warning-line"></i><span>{{ stockError }}</span>
                    </div>

                    <!-- ========== BODY ========== -->
                    <div class="cf-dialog-body cf-dialog-body--flush">
                        <!-- Desktop table -->
                        <div class="table-scroll-wrap art-scroll hidden lg:block">
                            <table class="art-table">
                                <thead>
                                    <tr>
                                        <th class="th" style="width:5rem">N°</th>
                                        <th class="th">Article / Service</th>
                                        <th v-if="showStockColumn" class="th th--right" style="width:11rem">Stock disponible</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-if="paginatedData.length">
                                        <tr v-for="(item, index) in paginatedData" :key="item.pid"
                                            class="table-row cf-row-clickable"
                                            :class="{ 'cf-row-active': activeItemId === item.pid }"
                                            @dblclick="handleSelectAndClose(item)" @click="setActiveItem(item)">
                                            <td class="td row-number">{{ paginationStart + index }}</td>
                                            <td class="td">
                                                <div class="cf-art-cell">
                                                    <span class="cf-art-name">{{ item.libelle }}</span>
                                                    <span v-if="item.destination" class="cf-badge" :class="getDestinationBadgeClass(item.destination)">{{ formatDestination(item.destination) }}</span>
                                                </div>
                                            </td>
                                            <td v-if="showStockColumn" class="td td--right">
                                                <span v-if="item.stock !== undefined" class="cf-badge" :class="getStockLevelClass(item.stock)">{{ formatStock(item.stock) }}</span>
                                                <span v-else class="cf-muted">-</span>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-else>
                                        <td :colspan="showStockColumn ? 3 : 2" class="td">
                                            <div class="empty-state">
                                                <div class="empty-state__icon"><i class="ri-inbox-line"></i></div>
                                                <p class="empty-state__title">Aucun article trouvé</p>
                                                <p class="empty-state__sub">{{ searchQuery ? 'Essayez de modifier vos critères de recherche' : 'Ajoutez un nouvel article' }}</p>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Mobile cards -->
                        <div class="lg:hidden art-scroll cf-cards">
                            <template v-if="paginatedData.length">
                                <div v-for="(item, index) in paginatedData" :key="item.pid"
                                     class="cf-card" :class="{ 'cf-card--active': activeItemId === item.pid }"
                                     @click="setActiveItem(item)">
                                    <div class="cf-card-head">
                                        <div class="cf-card-headmain">
                                            <span class="cf-card-index">{{ paginationStart + index }}</span>
                                            <span class="cf-card-title">{{ item.libelle }}</span>
                                        </div>
                                        <button @click.stop="handleSelectAndClose(item)" class="cf-card-select" aria-label="Sélectionner"><i class="ri-check-line"></i></button>
                                    </div>
                                    <div class="cf-card-body" v-if="item.destination || (showStockColumn && item.stock !== undefined)">
                                        <div v-if="item.destination">
                                            <p class="cf-card-lbl">Destination</p>
                                            <span class="cf-badge" :class="getDestinationBadgeClass(item.destination)">{{ formatDestination(item.destination) }}</span>
                                        </div>
                                        <div v-if="showStockColumn && item.stock !== undefined" class="cf-card-stock">
                                            <p class="cf-card-lbl">Stock</p>
                                            <span class="cf-badge" :class="getStockLevelClass(item.stock)">{{ formatStock(item.stock) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div v-else class="empty-state">
                                <div class="empty-state__icon"><i class="ri-inbox-line"></i></div>
                                <p class="empty-state__title">Aucun article trouvé</p>
                            </div>
                        </div>
                    </div>

                    <!-- ========== FOOTER ========== -->
                    <div class="cf-dialog-foot cf-dialog-foot--split">
                        <p class="cf-foot-info">{{ paginationStart }}–{{ paginationEnd }} sur {{ filteredData.length }}</p>
                        <div class="cf-foot-actions">
                            <div class="pagination" v-if="totalPages > 1">
                                <button class="page-btn page-btn--nav" @click="currentPage--" :disabled="currentPage === 1"><i class="ri-arrow-left-s-line"></i></button>
                                <template v-for="pageNum in displayedPages" :key="pageNum">
                                    <span v-if="pageNum === '...'" class="cf-ellipsis">…</span>
                                    <button v-else class="page-btn" :class="{ 'page-btn--active': currentPage === pageNum }" @click="currentPage = pageNum">{{ pageNum }}</button>
                                </template>
                                <button class="page-btn page-btn--nav" @click="currentPage++" :disabled="currentPage === totalPages || totalPages === 0"><i class="ri-arrow-right-s-line"></i></button>
                            </div>
                            <button @click="handleClearSelection" class="ti-btn ti-btn-light"><i class="ri-eraser-line align-middle"></i> Effacer la sélection</button>
                            <button @click="close" class="ti-btn ti-btn-secondary-full">Fermer</button>
                        </div>
                    </div>

                </div>
            </Transition>
        </div>
    </Transition>
</template>

<script>
    const axios = require('axios');
    import { randomString } from '@/allfunction';
    import * as JwtService from '@/common/jwt.service';


    // Debounce utility
    function debounce(fn, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    export default {
        /* eslint-disable */
        name: 'ArticleSelectionModal',

        components: {
        },

        props: {
            // Affiche le bouton « Actualiser » ; le parent recharge la liste sur l'évènement "actualiser"
            refreshable: { type: Boolean, default: false },
        },

        data() {
            return {
                isVisible: false,
                isLoading: false,
                showActionSheet: false,
                titleModal: '',
                actionType: '',
                filterType: '',
                searchQuery: '',
                currentPage: 1,
                perPage: 20,
                perPageOptions: [10, 20, 50, 100, 200],
                dataTable: [],
                activeItem: null,
                itemType: '', // 'cat', 'unite', 'emballage'
                stockError: '',
                isRefreshing: false,
            };
        },

        computed: {
            showAddButton() {
                return this.actionType !== 'vente' && this.itemType;
            },

            showStockColumn() {
                return this.itemType === 'article' || this.itemType === 'product';
            },

            showCategoryIcon() {
                return ['cat', 'unite', 'emballage'].includes(this.itemType);
            },

            activeItemId() {
                return this.activeItem ? this.activeItem.pid : null;
            },

            filteredData() {
                let data = [...this.dataTable];

                // Apply destination filter
                if (this.filterType === 'vente') {
                    data = data.filter(item =>
                        item.destination === "VENTE" ||
                        item.destination === "ACHAT_ET_VENTE"
                    );
                } else if (this.filterType === 'achat') {
                    data = data.filter(item =>
                        item.destination === "ACHAT" ||
                        item.destination === "ACHAT_ET_VENTE"
                    );
                }

                // Apply search filter
                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase().trim();
                    data = data.filter(item =>
                        item.libelle?.toString().toLowerCase().includes(query)
                    );
                }

                return data;
            },

            paginatedData() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredData.slice(start, start + this.perPage);
            },

            totalPages() {
                return Math.ceil(this.filteredData.length / this.perPage) || 1;
            },

            displayedPages() {
                const pages = [];
                const total = this.totalPages;
                const current = this.currentPage;

                if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                    if (current <= 3) {
                        pages.push(1, 2, 3, 4, '...', total);
                    } else if (current >= total - 2) {
                        pages.push(1, '...', total - 3, total - 2, total - 1, total);
                    } else {
                        pages.push(1, '...', current - 1, current, current + 1, '...', total);
                    }
                }
                return pages;
            },

            paginationStart() {
                return this.filteredData.length === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
            },

            paginationEnd() {
                return Math.min(this.currentPage * this.perPage, this.filteredData.length);
            },

            filterText() {
                if (this.filterType === 'vente') return 'Articles disponibles pour la vente';
                if (this.filterType === 'achat') return 'Articles disponibles pour l\'achat';
                return '';
            },
        },

        watch: {
            searchQuery() {
                this.currentPage = 1;
            },
            perPage() {
                this.currentPage = 1;
            },
        },

        created() {
            this.handleSearchDebounced = debounce(this.handleSearch, 300);
        },

        methods: {
            show(title, actionType, data, filterType = '') {
                this.titleModal = title;
                this.actionType = actionType;
                this.filterType = filterType;
                this.dataTable = Array.isArray(data) ? data : [];
                this.resetState();

                // Detect item type from data structure
                if (this.dataTable.length > 0) {
                    const firstItem = this.dataTable[0];
                    if (firstItem.destination) {
                        this.itemType = 'article';
                    } else if (firstItem.typ) {
                        this.itemType = firstItem.typ;
                    }
                }

                this.isVisible = true;
                document.body.style.overflow = 'hidden';

                // Focus search input after modal is shown
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
                this.closeActionSheet();
            },

            resetState() {
                this.searchQuery = '';
                this.currentPage = 1;
                this.activeItem = null;
                this.stockError = '';
                this.isRefreshing = false;
            },

            handleRefresh() {
                if (this.isRefreshing) return;
                this.stockError = '';
                this.isRefreshing = true;
                this.$emit('actualiser');
            },

            // Appelé par le parent à la fin du rechargement (liste = null en cas d'échec)
            finActualisation(liste, erreur = '') {
                if (Array.isArray(liste)) {
                    this.dataTable = liste;
                    this.currentPage = 1;
                    this.activeItem = null;
                }
                this.stockError = erreur;
                this.isRefreshing = false;
            },

            async handleSelectAndClose(item) {
                if (!item) {
                    this.handleClearSelection();
                    return;
                }

                if (this.isLoading) return;

                this.stockError = '';
                this.isLoading = true;
                try {
                    // Load stock data if needed
                    if (item.pid && item.pidmag) {
                        await this.loadStockData(item);
                    }

                    const selection = {
                        type: this.actionType,
                        value: item,
                        itemType: this.itemType,
                        dataTable: this.dataTable
                    };

                    this.$emit('elementSelectionnearticle', selection);
                    this.close();

                } catch (error) {
                    console.error('Error selecting item:', error);
                    this.stockError = `Impossible de lire le stock réel de « ${item.libelle} ». Vérifiez la connexion puis réessayez.`;
                    this.$toast?.error('Erreur lors de la sélection');
                } finally {
                    this.isLoading = false;
                }
            },

            handleClearSelection() {
                const selection = {
                    type: this.actionType,
                    value: null,
                    itemType: this.itemType,
                    dataTable: []
                };
                this.$emit('elementSelectionnearticle', selection);
                this.close();
            },

            // Lit le stock réel (dbstcks) de l'article : la liste reçue vient d'un cache serveur,
            // son rstck peut être périmé. Lève une erreur si le stock n'a pas pu être lu.
            async loadStockData(item) {
                const url = `${process.env.VUE_APP_API_URL}/egl745874pstock771714dfe/ar/${item.pid}/${item.pidmag}/majstck/${this.$store.state.appsociete}/${randomString()}`;
                const response = await axios.get(url, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${JwtService.getToken()}`,
                        'csrftoken': this.$store.state.appsociete
                    },
                    withCredentials: true
                });

                if (!response.data.success) {
                    throw new Error('Stock introuvable');
                }

                item.stock = response.data.m.stck;
                item.rstck = response.data.m.stck;
                // Update item in dataTable
                const index = this.dataTable.findIndex(i => i.pid === item.pid);
                if (index !== -1) {
                    this.dataTable[index].stock = item.stock;
                    this.dataTable[index].rstck = item.stock;
                }
            },

            handleAdd() {
                let title = '';
                let emptyItem = {};

                switch (this.itemType) {
                    case 'cat':
                        title = 'Enregistrer un Catalogue';
                        emptyItem = {
                            pid: '',
                            intitule: '',
                            typ: this.itemType,
                            taux: 0,
                            typreg: "CAISSE",
                            qteengros: 0,
                            qteendetail: 0
                        };
                        break;

                    case 'unite':
                        title = 'Enregistrer une unité';
                        emptyItem = {
                            pid: '',
                            intitule: '',
                            typ: this.itemType,
                            taux: 0,
                            typreg: "CAISSE",
                            qteengros: 0,
                            qteendetail: 0
                        };
                        break;

                    case 'emballage':
                        title = 'Enregistrer un emballage';
                        emptyItem = {
                            pid: '',
                            intitule: '',
                            typ: this.itemType,
                            taux: 0,
                            typreg: "",
                            qteengros: 1,
                            qteendetail: 0
                        };
                        break;

                    default:
                        title = 'Nouvel article';
                        emptyItem = {
                            pid: '',
                            libelle: '',
                            typ: this.itemType
                        };
                }

                if (this.$refs.modalForm) {
                    this.$refs.modalForm.show(title, 'add', this.itemType, emptyItem);
                }
            },

            handleSearch() {
                // Debounced search logic if needed
            },

            clearSearch() {
                this.searchQuery = '';
            },

            setActiveItem(item) {
                this.activeItem = item;
                //this.showActionSheet = true;
                this.showActionSheet = false;
            },

            closeActionSheet() {
                this.showActionSheet = false;
                this.activeItem = null;
            },

            getDestinationBadgeClass(destination) {
                switch (destination) {
                    case 'VENTE':
                        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
                    case 'ACHAT':
                        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                    case 'ACHAT_ET_VENTE':
                        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
                    default:
                        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
                }
            },

            formatDestination(destination) {
                const map = {
                    'VENTE': 'Vente',
                    'ACHAT': 'Achat',
                    'ACHAT_ET_VENTE': 'Achat & Vente'
                };
                return map[destination] || destination;
            },

            getStockLevelClass(stock) {
                if (stock > 10) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
                if (stock > 0) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            },

            formatStock(stock) {
                return typeof stock === 'number' ? stock.toLocaleString('fr-FR') : stock;
            },

            async loadData() {
                // Reload data after modal operations
                // This would be called when child modal closes
            },
        },

        beforeUnmount() {
            document.body.style.overflow = '';
        },
    };
</script>

<style scoped>
/* Charte cf-/ds- : tokens uniquement (light-only) */
.cf-overlay { position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgb(15 23 42 / 0.45); }
.cf-dialog { width:100%; background:#fff; border-radius:var(--cf-radius); box-shadow:var(--cf-shadow-md); display:flex; flex-direction:column; max-height:92vh; overflow:hidden; }
.cf-dialog--lg { max-width:880px; }
.cf-dialog-head { display:flex; align-items:center; justify-content:space-between; padding:1rem 1.25rem; border-bottom:1px solid var(--cf-border); flex-shrink:0; }
.cf-dialog-title { margin:0; font-size:0.95rem; font-weight:700; color:var(--cf-ink-strong); }
.cf-dialog-close { height:2rem; width:2rem; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); border:none; background:var(--cf-surface-alt); color:var(--cf-muted); cursor:pointer; transition:all .15s; }
.cf-dialog-close:hover { background:#fee2e2; color:rgb(var(--danger)); }
.cf-dialog-toolbar { padding:0.75rem 1.25rem; border-bottom:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-body { padding:1.1rem 1.25rem; overflow-y:auto; flex:1; }
.cf-dialog-body--flush { padding:0; }
.cf-dialog-foot { display:flex; align-items:center; justify-content:flex-end; gap:0.6rem; padding:0.9rem 1.25rem; border-top:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-foot--split { justify-content:space-between; flex-wrap:wrap; }

.cf-stock-error { display:flex; align-items:center; gap:0.6rem; margin:0.75rem 1.25rem 0; padding:0.7rem 0.9rem; border-radius:var(--cf-radius-sm); font-size:0.82rem; border:1px solid #fecaca; background:#fef2f2; color:#b91c1c; flex-shrink:0; }

/* Toolbar */
.cf-toolbar-row { display:flex; gap:0.75rem; align-items:center; }
.search-wrap { position:relative; flex:1; }
.search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--cf-faint); }
.search-input { width:100%; padding:9px 34px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); font-size:0.82rem; color:var(--cf-ink); outline:none; transition:border-color 0.2s; }
.search-input:focus { border-color:rgb(var(--primary)); box-shadow:0 0 0 3px rgb(var(--primary) / 0.10); }
.search-clear { position:absolute; right:8px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:var(--cf-faint); }
.cf-toolbar-meta { display:flex; align-items:center; justify-content:space-between; margin-top:0.7rem; gap:0.75rem; }
.cf-count { font-size:0.8rem; color:var(--cf-muted); margin:0; }
.cf-count span { color:var(--cf-ink-strong); font-weight:600; }
.cf-perpage { display:flex; align-items:center; gap:0.4rem; font-size:0.8rem; color:var(--cf-muted); }
.cf-select-wrap { position:relative; }
.cf-select { appearance:none; padding-right:28px; cursor:pointer; }
.cf-select--sm { height:34px; padding:0 28px 0 10px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; font-size:0.8rem; color:var(--cf-ink); }
.cf-select-arrow { position:absolute; right:10px; top:50%; transform:translateY(-50%); color:var(--cf-faint); pointer-events:none; }

/* Table */
.table-scroll-wrap { overflow:auto; }
.art-scroll { max-height:52vh; }
.art-table { width:100%; text-align:left; font-size:13px; border-collapse:collapse; min-width:420px; }
.th { position:sticky; top:0; z-index:2; padding:0.7rem 1rem; font-weight:600; font-size:0.75rem; color:var(--cf-ink); background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); white-space:nowrap; letter-spacing:0.02em; text-align:left; }
.th--right { text-align:right; }
.table-row { border-bottom:1px solid var(--cf-surface-alt); transition:background-color 0.15s; }
.table-row:hover { background:var(--cf-surface); }
.cf-row-clickable { cursor:pointer; }
.cf-row-active { background:rgb(var(--primary) / 0.08) !important; }
.td { padding:0.65rem 1rem; font-size:0.82rem; color:var(--cf-ink); vertical-align:middle; }
.td--right { text-align:right; }
.row-number { color:var(--cf-faint); font-size:0.75rem; font-variant-numeric:tabular-nums; }
.cf-art-cell { display:flex; align-items:center; gap:0.6rem; flex-wrap:wrap; }
.cf-art-name { font-weight:500; color:var(--cf-ink-strong); }
.cf-muted { color:var(--cf-faint); }

/* Badges (couleurs fournies par getDestinationBadgeClass/getStockLevelClass) */
.cf-badge { display:inline-flex; align-items:center; padding:2px 9px; font-size:0.72rem; font-weight:600; border-radius:999px; white-space:nowrap; }

/* Empty state */
.empty-state { padding:2.5rem 1.5rem; text-align:center; }
.empty-state__icon { font-size:40px; color:var(--cf-faint); margin-bottom:14px; }
.empty-state__title { font-size:0.95rem; font-weight:600; color:var(--cf-ink-strong); margin:0; }
.empty-state__sub { font-size:0.8rem; color:var(--cf-muted); margin:4px 0 0; }

/* Mobile cards */
.cf-cards { padding:0.75rem; display:flex; flex-direction:column; gap:0.5rem; }
.cf-card { border:1px solid var(--cf-border); border-radius:var(--cf-radius-sm); overflow:hidden; background:#fff; cursor:pointer; }
.cf-card--active { border-color:rgb(var(--primary) / 0.5); box-shadow:0 0 0 2px rgb(var(--primary) / 0.12); }
.cf-card-head { display:flex; align-items:center; justify-content:space-between; padding:0.6rem 0.85rem; background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); }
.cf-card-headmain { display:flex; align-items:center; gap:0.5rem; min-width:0; }
.cf-card-index { flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; min-width:1.5rem; height:1.5rem; padding:0 5px; font-size:0.72rem; font-weight:700; color:#fff; background:rgb(var(--primary)); border-radius:999px; }
.cf-card-title { font-weight:600; color:var(--cf-ink-strong); font-size:0.85rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cf-card-select { flex-shrink:0; height:2rem; width:2rem; display:inline-flex; align-items:center; justify-content:center; border:none; background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); border-radius:var(--cf-radius-sm); cursor:pointer; }
.cf-card-body { padding:0.75rem 0.85rem; }
.cf-card-lbl { font-size:0.7rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--cf-muted); margin:0 0 4px; }
.cf-card-stock { margin-top:0.6rem; }

/* Footer */
.cf-foot-info { font-size:0.78rem; color:var(--cf-muted); margin:0; }
.cf-foot-actions { display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; justify-content:flex-end; }
.pagination { display:flex; align-items:center; gap:4px; }
.cf-ellipsis { padding:0 4px; color:var(--cf-faint); }
.page-btn { min-width:30px; height:30px; padding:0 8px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; color:var(--cf-ink); font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.15s; }
.page-btn:hover:not(:disabled) { border-color:rgb(var(--primary) / 0.35); background:var(--cf-surface); }
.page-btn:disabled { opacity:0.5; cursor:not-allowed; }
.page-btn--active { background:rgb(var(--primary)); border-color:rgb(var(--primary)); color:#fff; }
.page-btn--nav { width:34px; display:inline-flex; align-items:center; justify-content:center; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity 0.25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }
.modal-slide-enter-active, .modal-slide-leave-active { transition:all 0.25s ease; }
.modal-slide-enter-from, .modal-slide-leave-to { opacity:0; transform:translateY(-16px); }

.art-scroll::-webkit-scrollbar { width:6px; height:6px; }
.art-scroll::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }

@media (max-width:768px) { .cf-dialog--lg { max-width:100%; } .art-scroll { max-height:60vh; } }
</style>
